import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ChevronLeft, ChevronRight, X, Bookmark, Car } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getResume, clearResume } from "../lib/resumeBrowsing";
import { formatSAR } from "../lib/api";

export default function ResumeBrowsingBanner() {
  const { lang } = useI18n();
  const [data, setData] = useState({ vehicle: null, search: null });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setData(getResume());
  }, []);

  // Hide if nothing to resume, or user dismissed it for this session.
  if (dismissed) return null;
  if (!data.vehicle && !data.search) return null;
  if (sessionStorage.getItem("cartal:resume:dismissed") === "1") return null;

  const Arrow = lang === "ar" ? ChevronLeft : ChevronRight;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("cartal:resume:dismissed", "1"); } catch { /* ignore */ }
  };

  const handleClear = () => {
    clearResume();
    setData({ vehicle: null, search: null });
  };

  return (
    <div
      className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 mt-4"
      data-testid="resume-banner"
    >
      <div
        className="rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-sm border"
        style={{
          background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #2a1f10 100%)",
          borderColor: "rgba(201,165,92,0.4)",
        }}
      >
        <div className="flex items-center gap-3 shrink-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(201,165,92,0.18)", border: "1px solid rgba(201,165,92,0.5)" }}
          >
            <Clock className="w-5 h-5" style={{ color: "#d4b56e" }} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#d4b56e" }}>
              {lang === "ar" ? "تابع التصفّح" : "Resume browsing"}
            </div>
            <div className="text-xs" style={{ color: "rgba(232,225,207,0.7)" }}>
              {lang === "ar" ? "من حيث توقّفت" : "Pick up where you left off"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 flex-1 min-w-0">
          {data.vehicle && (
            <Link
              to={`/vehicle/${data.vehicle.id}`}
              className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-white/5 transition-colors group min-w-0 flex-1 sm:max-w-[420px]"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,165,92,0.25)" }}
              data-testid="resume-vehicle"
            >
              {data.vehicle.image ? (
                <img
                  src={data.vehicle.image}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Car className="w-6 h-6" style={{ color: "#d4b56e" }} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs" style={{ color: "rgba(232,225,207,0.65)" }}>
                  {lang === "ar" ? "آخر سيارة شاهدتها" : "Last vehicle you viewed"}
                </div>
                <div className="font-display text-sm truncate" style={{ color: "#f7eecd" }}>
                  {data.vehicle.title}
                </div>
                {data.vehicle.price_sar && (
                  <div className="text-xs font-semibold" style={{ color: "#d4b56e" }}>
                    {formatSAR(data.vehicle.price_sar)} SAR
                    {data.vehicle.city && <span style={{ color: "rgba(232,225,207,0.55)" }}> · {data.vehicle.city}</span>}
                  </div>
                )}
              </div>
              <Arrow
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                style={{ color: "#d4b56e" }}
              />
            </Link>
          )}

          {data.search && (
            <Link
              to={data.search.pathname + (data.search.search || "")}
              className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-white/5 transition-colors group min-w-0 flex-1 sm:max-w-[320px]"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,165,92,0.25)" }}
              data-testid="resume-search"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(201,165,92,0.18)" }}
              >
                <Bookmark className="w-5 h-5" style={{ color: "#d4b56e" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs" style={{ color: "rgba(232,225,207,0.65)" }}>
                  {lang === "ar" ? "آخر بحث" : "Last search"}
                </div>
                <div className="font-display text-sm truncate" style={{ color: "#f7eecd" }}>
                  {data.search.label || (lang === "ar" ? "السوق" : "Marketplace")}
                </div>
              </div>
              <Arrow
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                style={{ color: "#d4b56e" }}
              />
            </Link>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] px-2 py-1 rounded hover:bg-white/10 transition-colors"
            style={{ color: "rgba(232,225,207,0.55)" }}
            data-testid="resume-clear"
          >
            {lang === "ar" ? "مسح" : "Clear"}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
            aria-label={lang === "ar" ? "إخفاء" : "Dismiss"}
            data-testid="resume-dismiss"
          >
            <X className="w-4 h-4" style={{ color: "rgba(232,225,207,0.7)" }} />
          </button>
        </div>
      </div>
    </div>
  );
}
